// PurchasePipeline.jsx - Mobile only component with filtered activities and custom colors
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ThreadSahiIcon from "../../../Components/Common/ThreadSahiIcon";
import { useCurrency } from "../../../Hooks/useCurrency";
import { ChevronUp, ChevronDown, Plus } from "lucide-react";
import { useState } from "react";
import CustomTooltip from "../../../Components/Common/CustomTooltip";
import ThreadDropdown from "../../Threads/AddThreadDropdown";
import { checkUserRights, formatAmount } from "../../../Utils/Utilities";
import useIsMobile from "../../../Hooks/useIsMobile";
import { getConfigData } from "../../../Utils/Storage";

const PurchasePipeline = ({
  businessActivities = [],
  threadData = [],
  onThreadEntryOpen = () => { }
}) => {
  const navigate = useNavigate()
  const currencySymbol = useCurrency();
  const [expandedSections, setExpandedSections] = useState({});
  const [isThreadDDOpen, setIsThreadDDOpen] = useState(false);
  const isMobile = useIsMobile();
  const { data: agencyData } = useSelector(state => state.agencyData);
  const filteredActivities = businessActivities.filter(activity => activity && [4, 5, 6].includes(activity.id));

  if (filteredActivities.length === 0) {
    return null;
  }

  // Activity name map
  const activityNameMap = {
    "Purchase Inquiries": "Inquiries",
    "Purchase Orders": "POs",
    "Supplier Complaints": "Splr Complaints",
  };

  const toggleSection = (activityId) => {
    setExpandedSections(prev => ({
      ...prev,
      [activityId]: !prev[activityId]
    }));
  };

  // Get data from localStorage
  const getThreadStages = () => {
    try {
      const configData = getConfigData();
      const threadStages = JSON.parse(configData?.ThreadStages || '[]');
      return threadStages;
    } catch (error) {
      console.error('Error parsing thread stages:', error);
      return [];
    }
  };

  // Get real thread data for specific activity type
  const getThreadDataForType = (typeId) => {
    return threadData.filter(thread => thread.TypeID === typeId && thread.StatusID != 3);
  };

  // Calculate stage-wise breakdown
  const getStageBreakdown = (threads, stages) => {
    const stageBreakdown = {};

    stages.forEach(stage => {
      stageBreakdown[stage.id] = {
        name: stage.name,
        count: 0,
        value: 0,
        stageIndex: stage.stageIndex // Keep track of original stage position
      };
    });

    threads.forEach(thread => {
      const currentStageId = thread.CurrStageID;
      if (stageBreakdown[currentStageId]) {
        stageBreakdown[currentStageId].count += 1;
        stageBreakdown[currentStageId].value += parseFloat(thread.BizProspect || 0);
      }
    });

    return Object.values(stageBreakdown);
  };

  // Get stage color based on stage index and activity type
  const getStageColor = (stageIndex, activityId) => {
    if (activityId === 4) {

      const orangeColors = ['#f49966', '#e26f1f', '#bf5700', '#9a4000', '#7a3400'];
      return orangeColors[stageIndex] || '#f97316';
    } else if (activityId === 5) {

      const greenColors = ['#63d68c', '#2ea447', '#0b5d1e', '#073613', '#041f0b'];
      return greenColors[stageIndex] || '#22c55e';
    } else if (activityId === 6) {

      const blueColors = ['#4db3d1', '#2fa3c7', '#0c6980', '#094f66', '#06394d'];
      return blueColors[stageIndex] || '#0c6980';
    }
    // Fallback
    return '#6b7280';
  };

  // Function which will calculate activity details
  const getActivityDetails = (activity) => {
    const threadStages = getThreadStages();
    const activityStages = threadStages.find(thread => thread.TID === activity.id);

    // Get stages for this activity type with their original index
    const stages = activityStages?.Stages?.map((stage, index) => ({
      id: stage.SID,
      name: stage.S,
      stageIndex: index // Keep track of original position
    })) || [];

    // Get threads for this activity type
    const threadsForType = getThreadDataForType(activity.id);

    // Calculate stage-wise breakdown
    const stageBreakdown = getStageBreakdown(threadsForType, stages);

    // Calculate totals
    const totalValue = stageBreakdown.reduce((sum, stage) => sum + stage.value, 0);
    const totalCount = stageBreakdown.reduce((sum, stage) => sum + stage.count, 0);

    // Create stage bars data for individual divs
    const getStageSegments = (stages) => {
      const total = stages.reduce((sum, stage) => sum + stage.count, 0);

      if (total === 0) {
        // When no data, show all stages with equal width
        return stages.map(stage => ({
          ...stage,
          widthPercentage: 100 / stages.length, // Equal width for all stages
          color: getStageColor(stage.stageIndex, activity.id)
        }));
      }

      // When there is data, only show stages with actual counts
      const stagesWithData = stages.filter(stage => stage.count > 0);
      return stagesWithData.map(stage => ({
        ...stage,
        widthPercentage: total > 0 ? (stage.count / total) * 100 : 0,
        color: getStageColor(stage.stageIndex, activity.id)
      }));
    };

    return {
      totalValue,
      totalCount,
      stages: totalCount === 0 ? stageBreakdown : stageBreakdown.filter(stage => stage.count > 0 || stage.value > 0), // Show all stages only when no data
      stageSegments: getStageSegments(stageBreakdown) // Show segments based on data availability
    };
  };

  const getMobileFormatPrice = (price) => {
    if (isMobile) return formatAmount(price, agencyData.CountryID);
    return price
  }

  return (
    <div className="overflow-hidden bg-sahi-white rounded-xl pb-1">
      {/* Header */}
      {checkUserRights().canSeeSplrs == true &&
        <div className="text-xl p-4 font-semibold flex-shrink-0 flex items-center justify-between border-sahi-border-color text-text-color">
          <span>Purchase Pipeline</span>
          <ThreadDropdown
            minimalIcon={true}
            iconButton={false}
            connectionType="S"
            className={`relative !h-6 !w-6`}
            onThreadTypeSelect={(type) => onThreadEntryOpen(type)}
            externalOpen={isThreadDDOpen}
            onExternalOpenChange={setIsThreadDDOpen}
          />
        </div>}

      {/* Activity Sections - Only show filtered activities */}
      <div
      // className="bg-bg-grey py-4 mx-4 rounded-xl"
      >
        {filteredActivities.map((activity, index) => {
          const details = getActivityDetails(activity);
          const isExpanded = expandedSections[activity.id];
          const hasStages = details.stages && details.stages.length > 0;

          return (
            <div
              key={activity.id}
            >
              {/* Activity Header */}
              <div className="p-4 py-0">
                <div className="flex items-center justify-between mb-3" onClick={() => navigate(`/threads?tid=${activity.id}`)}>
                  <div className="flex items-center gap-2">
                    <ThreadSahiIcon tID={activity.id} className="h-4 w-4" />
                    <span className="text-text-color font-semibold text-base">
                      {activityNameMap[activity.name] || activity.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 justify-center">
                    {/* Total Value and Count - Always Show */}
                    <div className="text-base font-bold text-sahi-primary-color">
                      <span className="text-sm font-normal text-sahi-grey mr-1">
                        {currencySymbol}
                      </span>
                      {hasStages ? getMobileFormatPrice(details.totalValue) : getMobileFormatPrice(activity.value)}
                    </div>
                    <div className="text-sm text-gray-500">
                      ({hasStages ? details.totalCount : (activity.open + activity.delayed)})
                    </div>
                    {/* Show expand button only if there are stages */}
                    {hasStages && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection(activity.id)
                        }}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      >
                        {isExpanded ? <ChevronUp strokeWidth={3} className="h-4 w-4" /> : <ChevronDown strokeWidth={3} className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress Bar - Always Show */}
                {hasStages && <div className="mb-4">
                  <div className="w-full h-[15px] overflow-hidden flex">
                    {details.stageSegments
                      .sort((a, b) => b.stageIndex - a.stageIndex) // Sort in reverse order
                      .map((segment, index) => (
                        <CustomTooltip
                          key={segment.stageIndex}
                          content={(data) => (
                            <div className="space-y-2 pb-3">
                              <div className="flex items-center gap-2 p-3 bg-bg-grey">
                                <div
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{ backgroundColor: data.color }}
                                />
                                <h4 className="text-sm font-semibold text-sahi-primary-color">{data.name}</h4>
                              </div>
                              <div className="space-y-1.5 text-xs px-3">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Value:</span>
                                  <span className="font-medium text-gray-800">
                                    <span className="font-normal text-sahi-grey mr-1">
                                      {currencySymbol}
                                    </span>
                                    {data.value.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Count:</span>
                                  <span className="font-medium text-gray-800">{data.count}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Avg. Value:</span>
                                  <span className="font-medium text-gray-800">
                                    <span className="font-normal text-sahi-grey mr-1">
                                      {currencySymbol}
                                    </span>
                                    {data.count > 0 ? Math.round(data.value / data.count).toLocaleString() : '0'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        >
                          {({ onMouseEnter, onMouseLeave }) => (
                            <div
                              className="h-full cursor-pointer transition-opacity hover:opacity-80"
                              style={{
                                width: `${segment.widthPercentage}%`,
                                backgroundColor: segment.color,
                                borderTopLeftRadius: index === 0 ? '0.275rem' : '0',
                                borderBottomLeftRadius: index === 0 ? '0.275rem' : '0',
                                borderTopRightRadius: index === details.stageSegments.length - 1 ? '0.275rem' : '0',
                                borderBottomRightRadius: index === details.stageSegments.length - 1 ? '0.275rem' : '0'
                              }}
                              onMouseEnter={(e) => onMouseEnter(segment, e)}
                              onMouseLeave={onMouseLeave}
                              onClick={() => navigate(`/threads?tid=${activity.id}&stg=${segment.stageIndex + 1}`)}

                            />
                          )}
                        </CustomTooltip>
                      ))}
                  </div>
                </div>}

                {/* Expanded Stage Details */}
                {hasStages && isExpanded && (
                  <div className="space-y-3 bg-background rounded-lg p-3 mb-3">
                    {details.stages
                      .sort((a, b) => b.stageIndex - a.stageIndex) // Sort in reverse order (later stages first)
                      .map((stage) => (
                        <div key={stage.stageIndex} className="flex justify-between sahi-radius items-center p-3 bg-sahi-white"
                          onClick={() => navigate(`/threads?tid=${activity.id}&stg=${stage.stageIndex + 1}`)}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{
                                backgroundColor: getStageColor(stage.stageIndex, activity.id)
                              }}
                            />
                            <span className="text-sm font-medium text-gray-700">{stage.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-semibold text-gray-800">
                              {currencySymbol}{getMobileFormatPrice(stage.value)}
                            </span>
                            <span className="text-sm text-gray-500">({stage.count})</span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PurchasePipeline;